import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CheckCircle, ExternalLink, MapPin, Globe, Share2, Star, FileText } from "lucide-react";

const SEOGuidePage = () => {
  return (
    <>
      <Helmet>
        <title>Off-Page SEO & Local SEO Guide | WINTRUST SOLUTIONS</title>
        <meta name="description" content="Complete off-page SEO and local SEO guide for WINTRUST SOLUTIONS. Google Business Profile optimization, backlink strategies, and local directory listings." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <section className="bg-navy py-12">
        <div className="container-custom">
          <nav className="text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-gold">Home</Link> / <span className="text-white">SEO Guide</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-white font-heading">
            Off-Page SEO & Local SEO Action Plan
          </h1>
          <p className="text-slate-300 mt-4 max-w-2xl">
            Step-by-step guide to boost WINTRUST SOLUTIONS' online visibility, local search rankings, and lead generation in Coimbatore.
          </p>
        </div>
      </section>

      <section className="bg-white section-padding">
        <div className="container-custom max-w-4xl space-y-12">

          {/* Google Business Profile */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <MapPin className="text-gold" size={20} />
              </div>
              <h2 className="text-2xl font-semibold text-navy font-heading">1. Google Business Profile Optimization</h2>
            </div>
            <p className="text-slate-600 mb-4">
              Your Google Business Profile (GBP) is the single most important factor for showing up in local "near me" searches and Google Maps results.
            </p>
            <div className="space-y-3">
              {[
                { task: "Claim & Verify your Google Business Profile", link: "https://business.google.com/", linkText: "Go to Google Business" },
                { task: "Set Business Name: \"WINTRUST SOLUTIONS\"" },
                { task: "Set Category: \"Tax Consultant\" (primary), add secondary: \"Financial Consultant\", \"Accounting Firm\"" },
                { task: "Add complete address with Coimbatore, Tamil Nadu pincode" },
                { task: "Add phone number: +91 89409 88776" },
                { task: "Add website URL: https://wintrustsolutions.com" },
                { task: "Set business hours: Mon-Sat 9:00 AM - 6:00 PM" },
                { task: "Write a keyword-rich business description (750 characters max) including: tax consultant, Coimbatore, GST registration, ITR filing, TDS" },
                { task: "Add 10-15 high-quality photos: office exterior, interior, team, logo" },
                { task: "Create your first Google Post announcing your services" },
                { task: "Add all services individually: GST Registration, GST Return Filing, ITR Filing, TDS Filing, Business Registration, Health Insurance Advisory" },
                { task: "Enable messaging and Q&A on your profile" },
                { task: "Ask your first 5-10 clients to leave Google Reviews (aim for 4.5+ stars)" },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={18} />
                  <div>
                    <span className="text-navy">{item.task}</span>
                    {item.link && (
                      <a href={item.link} target="_blank" rel="noopener noreferrer" className="ml-2 text-gold text-sm hover:underline inline-flex items-center gap-1">
                        {item.linkText} <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Local Directory Listings */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <Globe className="text-gold" size={20} />
              </div>
              <h2 className="text-2xl font-semibold text-navy font-heading">2. Local Directory Listings (Citations)</h2>
            </div>
            <p className="text-slate-600 mb-4">
              Consistent NAP (Name, Address, Phone) across directories strengthens local SEO. List your business on these platforms:
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { name: "JustDial", url: "https://www.justdial.com/", priority: "High" },
                { name: "Sulekha", url: "https://www.sulekha.com/", priority: "High" },
                { name: "IndiaMART", url: "https://www.indiamart.com/", priority: "High" },
                { name: "TradeIndia", url: "https://www.tradeindia.com/", priority: "Medium" },
                { name: "Yellow Pages India", url: "https://www.yellowpages.co.in/", priority: "Medium" },
                { name: "Bing Places", url: "https://www.bingplaces.com/", priority: "Medium" },
                { name: "Apple Maps (Maps Connect)", url: "https://mapsconnect.apple.com/", priority: "Medium" },
                { name: "Facebook Business Page", url: "https://www.facebook.com/business/", priority: "High" },
                { name: "LinkedIn Company Page", url: "https://www.linkedin.com/company/setup/", priority: "High" },
                { name: "Instagram Business", url: "https://business.instagram.com/", priority: "Medium" },
              ].map((dir, i) => (
                <a key={i} href={dir.url} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="text-slate-300" size={16} />
                    <span className="text-navy font-medium">{dir.name}</span>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${dir.priority === "High" ? "bg-gold/10 text-gold" : "bg-slate-200 text-slate-500"}`}>
                    {dir.priority}
                  </span>
                </a>
              ))}
            </div>
            <div className="mt-4 p-4 bg-gold/5 border border-gold/20 rounded-lg">
              <p className="text-sm text-navy"><strong>Important:</strong> Use the EXACT same business name, address, and phone number on every listing. Inconsistencies hurt local SEO.</p>
            </div>
          </div>

          {/* Backlink Strategy */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <Share2 className="text-gold" size={20} />
              </div>
              <h2 className="text-2xl font-semibold text-navy font-heading">3. Backlink Building Strategy</h2>
            </div>
            <p className="text-slate-600 mb-4">
              Quality backlinks from relevant websites signal authority to search engines. Focus on these strategies:
            </p>
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-2">Guest Posting</h3>
                <p className="text-slate-600 text-sm">Write articles on tax tips for Coimbatore business blogs, startup communities, and financial websites. Include a link back to your relevant service page.</p>
                <p className="text-slate-500 text-xs mt-2">Target: 2-3 guest posts per month</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-2">Industry Associations</h3>
                <p className="text-slate-600 text-sm">Join Coimbatore Chamber of Commerce, MSME associations, CA/Tax Practitioner forums. Most have member directories with backlinks.</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-2">Quora & Reddit Answers</h3>
                <p className="text-slate-600 text-sm">Answer GST, ITR, and tax-related questions on Quora with helpful answers. Link to your blog posts naturally when relevant.</p>
                <p className="text-slate-500 text-xs mt-2">Target: 5-10 detailed answers per week</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-navy mb-2">Local News & PR</h3>
                <p className="text-slate-600 text-sm">Offer expert commentary on tax deadlines, GST changes, and budget updates to local Coimbatore media outlets and online news sites.</p>
              </div>
            </div>
          </div>

          {/* Review Strategy */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <Star className="text-gold" size={20} />
              </div>
              <h2 className="text-2xl font-semibold text-navy font-heading">4. Review Generation Strategy</h2>
            </div>
            <div className="space-y-3">
              {[
                "After completing each service, send a WhatsApp message with a direct Google Review link",
                "Create a short URL (bit.ly) for your Google Review page and print on business cards",
                "Respond to EVERY review (positive or negative) within 24 hours",
                "Target: Collect 3-5 new reviews per month consistently",
                "Ask for reviews mentioning specific services (e.g., 'GST registration') for keyword relevance",
                "Display your best reviews on the website testimonials section",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-navy text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Content Marketing */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-navy rounded-lg flex items-center justify-center">
                <FileText className="text-gold" size={20} />
              </div>
              <h2 className="text-2xl font-semibold text-navy font-heading">5. Content Marketing Plan</h2>
            </div>
            <p className="text-slate-600 mb-4">Your blog already has 12 posts. Keep the momentum going:</p>
            <div className="space-y-3">
              {[
                "Publish 2 new blog posts per month on trending tax topics",
                "Share every new blog post on LinkedIn, Facebook, and WhatsApp Business",
                "Create short video tips (1-2 min) on GST/ITR topics for Instagram Reels and YouTube Shorts",
                "Repurpose blog content into infographics for social media",
                "Send a monthly newsletter to past clients with tax tips and deadline reminders",
                "Create seasonal content around ITR deadlines (July), GST annual return (Dec), and budget analysis (Feb)",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <CheckCircle className="text-gold flex-shrink-0 mt-0.5" size={18} />
                  <span className="text-navy text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Wins */}
          <div className="bg-navy rounded-xl p-8 text-white">
            <h2 className="text-2xl font-semibold mb-4 font-heading text-gold">Quick Wins (Do This Week)</h2>
            <ol className="space-y-3 list-decimal list-inside">
              <li className="text-slate-300">Claim and fully optimize your Google Business Profile</li>
              <li className="text-slate-300">List on JustDial, Sulekha, and LinkedIn with consistent NAP</li>
              <li className="text-slate-300">Ask your 5 best clients for Google Reviews</li>
              <li className="text-slate-300">Create a Facebook Business Page and share your blog posts</li>
              <li className="text-slate-300">Answer 5 tax-related questions on Quora with links to your blog</li>
            </ol>
          </div>

        </div>
      </section>
    </>
  );
};

export default SEOGuidePage;
